import Icon from '@/components/ui/AppIcon';

interface SecurityFeature {
  title: string;
  description: string;
  icon: string;
}

interface SecurityCenterProps {
  className?: string;
}

export default function SecurityCenter({ className = '' }: SecurityCenterProps) {
  const securityFeatures: SecurityFeature[] = [
    {
      title: 'End-to-End Encryption',
      description:
        'All communications through ParkPing are encrypted using bank-level security protocols. Your data is protected at every step.',
      icon: 'LockClosedIcon',
    },
    {
      title: 'Phone Number Privacy',
      description:
        'Your phone number is never displayed or shared. Our call masking technology ensures complete anonymity for both parties.',
      icon: 'ShieldCheckIcon',
    },
    {
      title: 'Data Protection Compliance',
      description:
        'We comply with Indian data protection regulations and international privacy standards. Your information is stored securely and never sold.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Secure Payment Processing',
      description:
        'All transactions are processed through PCI-DSS compliant payment gateways with multiple layers of security verification.',
      icon: 'CreditCardIcon',
    },
    {
      title: 'Account Security',
      description:
        'Two-factor authentication, secure login protocols, and instant deactivation options keep your account protected.',
      icon: 'FingerPrintIcon',
    },
    {
      title: 'Regular Security Audits',
      description:
        'Our systems undergo regular third-party security audits to ensure the highest standards of data protection and privacy.',
      icon: 'ShieldExclamationIcon',
    },
  ];

  return (
    <section className={`py-16 bg-white ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Security & Privacy Center
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Your privacy and security are our top priorities. Learn how we
              protect your information and maintain the highest standards of
              data security.
            </p>
          </div>

          {/* Security Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-[#42AF47]/40 hover:shadow-md transition"
              >
                <div className="w-12 h-12 bg-[#42AF47]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon
                    name={feature.icon as any}
                    size={24}
                    className="text-[#42AF47]"
                  />
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Policy Box */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <Icon
                name="InformationCircleIcon"
                size={24}
                className="text-[#42AF47]"
              />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Privacy Policy & Terms
                </h3>

                <p className="text-gray-600 mb-4">
                  We believe in transparency. Our privacy policy and terms of
                  service are written in clear, understandable language. We
                  collect only the minimum data necessary to provide our service
                  and never share your information with third parties without
                  your explicit consent.
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-[#42AF47] text-white rounded-lg hover:brightness-110 transition">
                    Read Privacy Policy
                  </button>

                  <button className="px-4 py-2 bg-white border border-gray-200 text-gray-900 rounded-lg hover:border-[#42AF47] transition">
                    View Terms of Service
                  </button>

                  <button className="px-4 py-2 bg-white border border-gray-200 text-gray-900 rounded-lg hover:border-[#42AF47] transition">
                    Data Protection Guidelines
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
